/**
 * Permission codes the C0 tax-spine screens declare an interest in.
 *
 * These are the dot-strings `internal/tax/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6, design §6.1). They live in the module rather
 * than in `src/lib/permissions.ts` because that file is shared by every screen
 * in the app and this module is the only consumer — the same split
 * `@/modules/consol/permissions` and `@/modules/gl/permissions` already make.
 *
 * The split the server draws is honoured here rather than collapsed, because
 * each half is a different act:
 *
 *  - **Reading** a jurisdiction, a registration or a return is what an Auditor
 *    does all day. Every route below is gated on a *read* code so an Auditor can
 *    open every one of these screens and change nothing on them.
 *  - **`tax.pack.accept`** is not "save". It moves the statutory rules the whole
 *    ledger computes from, for every document dated on or after the effective
 *    date. The server demands step-up MFA for it; the screen makes the act
 *    deliberate to match (`PackAcceptDialog.vue`).
 *  - **`tax.return.approve` conflicts with `tax.return.prepare`** in the SoD
 *    table (design §6.1), so the two are never checked together to enable one
 *    control: whoever computed the return must not be the person certifying it.
 *  - **`tax.return.file`** likewise carries step-up: recording a filing and a
 *    payment is an assertion to a tax authority.
 *
 * Actions are gated on the action, never on the route, so a read-only user is
 * never bounced from a screen they are entitled to read.
 */

export const TAX_SPINE_PERMISSIONS = {
  jurisdictionRead: 'tax.jurisdiction.read',

  packRead: 'tax.pack.read',
  packActivate: 'tax.pack.activate',
  /** Step-up MFA server-side. */
  packAccept: 'tax.pack.accept',

  registrationRead: 'tax.registration.read',
  registrationManage: 'tax.registration.manage',

  certificateRead: 'tax.certificate.read',
  certificateManage: 'tax.certificate.manage',

  returnRead: 'tax.return.read',
  returnPrepare: 'tax.return.prepare',
  returnApprove: 'tax.return.approve',
  /** Step-up MFA server-side. */
  returnFile: 'tax.return.file',
  returnExport: 'tax.return.export',

  chargeRead: 'tax.charge.read',

  /* ------------------------------------------------- other modules' codes */

  /**
   * `/tax/simulate` is guarded by `tax.code.read`, not by a code of its own:
   * simulating is reading the catalogue through the engine rather than by eye.
   */
  simulate: 'tax.code.read',

  /**
   * Registrations, certificates and charge pools are all per company, and the
   * API returns company **ids**. Without `org.company.read` the screens show the
   * id rather than inventing a name, and the company filter is hidden.
   */
  companyRead: 'org.company.read',
} as const

export type TaxSpinePermission =
  (typeof TAX_SPINE_PERMISSIONS)[keyof typeof TAX_SPINE_PERMISSIONS]
